import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as notesApi from "@/lib/api/notes";
import { queryKeys } from "@/lib/query-keys";
import type { Note } from "@/types/note";

interface UseDeleteNoteWithUndoOptions {
  /**
   * Duration in ms before the delete is committed.
   * Default: 5000 (5 seconds)
   */
  undoTimeout?: number;
  /**
   * Callback after successful deletion (after undo period expires)
   */
  onDeleted?: (noteId: string) => void;
  /**
   * Callback if user clicks undo
   */
  onUndo?: (noteId: string) => void;
}

/**
 * Hook for deleting notes with an undo toast.
 *
 * - Immediately removes the note from the UI (optimistic)
 * - Shows a toast with an "Undo" button
 * - If user doesn't undo within the timeout, commits the deletion
 * - If user clicks undo, restores the note to the cache
 *
 * @example
 * const { deleteNote, pendingDeletes, isDeleting } = useDeleteNoteWithUndo();
 *
 * <Button onClick={() => deleteNote(note)}>Delete</Button>
 */
export function useDeleteNoteWithUndo(
  options: UseDeleteNoteWithUndoOptions = {},
) {
  const { undoTimeout = 5000, onDeleted, onUndo } = options;

  const queryClient = useQueryClient();
  const pendingDeletesRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const deletedNotesRef = useRef<Map<string, Note>>(new Map());

  /**
   * Delete a note with undo capability
   */
  const deleteNote = useCallback(
    (note: Note) => {
      const noteId = note.id;

      // Store the note for potential restoration
      deletedNotesRef.current.set(noteId, note);

      // Optimistically remove from all caches
      queryClient.setQueriesData(
        { queryKey: queryKeys.notes.all },
        (oldData: unknown) => {
          if (!oldData) return oldData;

          // Handle array data (notes list)
          if (Array.isArray(oldData)) {
            return oldData.filter((n: Note) => n.id !== noteId);
          }

          // Handle home query data structure
          if (
            typeof oldData === "object" &&
            ("overdueNotes" in oldData ||
              "dueTodayNotes" in oldData ||
              "recentlyAdded" in oldData)
          ) {
            const homeData = oldData as {
              overdueNotes?: Note[];
              dueTodayNotes?: Note[];
              recentlyAdded?: Note[];
            };
            return {
              ...homeData,
              overdueNotes:
                homeData.overdueNotes?.filter((n) => n.id !== noteId) || [],
              dueTodayNotes:
                homeData.dueTodayNotes?.filter((n) => n.id !== noteId) || [],
              recentlyAdded:
                homeData.recentlyAdded?.filter((n) => n.id !== noteId) || [],
            };
          }

          return oldData;
        },
      );

      // Cancel any existing pending delete for this note
      const existingTimeout = pendingDeletesRef.current.get(noteId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Show toast with undo action
      const toastId = toast("Note deleted", {
        description: note.title || "Untitled note",
        action: {
          label: "Undo",
          onClick: () => {
            // Cancel the pending delete
            const timeout = pendingDeletesRef.current.get(noteId);
            if (timeout) {
              clearTimeout(timeout);
              pendingDeletesRef.current.delete(noteId);
            }

            // Restore the note to the cache
            const storedNote = deletedNotesRef.current.get(noteId);
            if (storedNote) {
              queryClient.setQueriesData(
                { queryKey: queryKeys.notes.all },
                (oldData: unknown) => {
                  if (!oldData) return oldData;

                  // Handle array data
                  if (Array.isArray(oldData)) {
                    if (oldData.some((n: Note) => n.id === noteId)) {
                      return oldData;
                    }
                    return [...oldData, storedNote];
                  }

                  // Handle home query - restore to recentlyAdded
                  if (
                    typeof oldData === "object" &&
                    ("overdueNotes" in oldData ||
                      "dueTodayNotes" in oldData ||
                      "recentlyAdded" in oldData)
                  ) {
                    const homeData = oldData as {
                      overdueNotes?: Note[];
                      dueTodayNotes?: Note[];
                      recentlyAdded?: Note[];
                    };
                    // Just invalidate the home query to refetch proper categorization
                    return homeData;
                  }

                  return oldData;
                },
              );
              // Invalidate home query to get correct categorization on restore
              queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
              deletedNotesRef.current.delete(noteId);
            }

            onUndo?.(noteId);
            toast.success("Note restored");
          },
        },
        duration: undoTimeout,
      });

      // Set up the actual deletion after timeout
      const timeoutId = setTimeout(async () => {
        pendingDeletesRef.current.delete(noteId);
        deletedNotesRef.current.delete(noteId);

        try {
          await notesApi.deleteNote(noteId);
          // Invalidate to ensure server state is synced
          queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
          onDeleted?.(noteId);
        } catch (error) {
          // Restore on error - just invalidate to refetch
          queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
          toast.dismiss(toastId);
          toast.error("Failed to delete note");
          console.error("Failed to delete note:", error);
        }
      }, undoTimeout);

      pendingDeletesRef.current.set(noteId, timeoutId);
    },
    [queryClient, undoTimeout, onDeleted, onUndo],
  );

  /**
   * Check if a note is pending deletion
   */
  const isDeleting = useCallback((noteId: string) => {
    return pendingDeletesRef.current.has(noteId);
  }, []);

  /**
   * Get set of note IDs that are pending deletion
   */
  const pendingDeletes = new Set(pendingDeletesRef.current.keys());

  return {
    deleteNote,
    isDeleting,
    pendingDeletes,
  };
}
